import {
  API_URL,
  type AdjustmentsResponse,
  type AdminOrderResponse,
  type AdminStats,
  type ApiErrorBody,
  type CatalogItem,
  type ContactInput,
  type CustomerPageResponse,
  type CustomerResponse,
  type EtransferAnswerInput,
  type MessagesResponse,
  type Order,
  type OrderNote,
  type OrderPageResponse,
  type OrderStatus,
  type PlaceOrderInput,
  type PlacedOrder,
  type Product,
  type ProductPatch,
  type PublicOrderView,
  type StockInput,
  type StoreSettings,
} from '@nsd/core';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type Token = () => Promise<string>;

async function request<T>(base: string, path: string, init: RequestInit = {}, token?: Token): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body) headers['content-type'] = 'application/json';
  if (token) headers['authorization'] = `Bearer ${await token()}`;
  let res: Response;
  try {
    res = await fetch(base + path, { ...init, headers: { ...headers, ...(init.headers as Record<string, string> | undefined) } });
  } catch {
    throw new ApiError(0, 'Could not reach the store. Check your connection and try again.');
  }
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, err.error ?? `Request failed (${res.status})`, err.fields ?? {});
  }
  return data as T;
}

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const put = (body: unknown): RequestInit => ({ method: 'PUT', body: JSON.stringify(body) });
const qs = (params: Record<string, string | undefined>) => {
  const s = new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => Boolean(e[1]))).toString();
  return s ? `?${s}` : '';
};

/** Storefront calls. No auth. */
export function publicApi(base = API_URL) {
  return {
    catalog: () => request<{ products: CatalogItem[] }>(base, '/catalog').then((r) => r.products),
    placeOrder: (input: PlaceOrderInput) => request<PlacedOrder>(base, '/orders', post(input)),
    getOrder: (number: number, token: string) => request<PublicOrderView>(base, `/orders/${number}${qs({ t: token })}`),
    sendEtransferAnswer: (input: EtransferAnswerInput) => request<{ ok: true }>(base, '/etransfer-answer', post(input)),
    contact: (input: ContactInput) => request<{ ok: true }>(base, '/contact', post(input)),
  };
}

/** Admin calls. `token` returns a fresh Cognito access token. */
export function adminApi(token: Token, base = API_URL) {
  const get = <T>(path: string) => request<T>(base, path, {}, token);
  const send = <T>(path: string, init: RequestInit) => request<T>(base, path, init, token);
  return {
    stats: () => get<AdminStats>('/admin/stats'),
    orders: (params: { status?: OrderStatus | undefined; cursor?: string | undefined } = {}) => get<OrderPageResponse>(`/admin/orders${qs(params)}`),
    order: (number: number) => get<AdminOrderResponse>(`/admin/orders/${number}`),
    setStatus: (number: number, status: OrderStatus, note?: string) => send<{ order: Order }>(`/admin/orders/${number}/status`, post({ status, note })),
    addShipment: (number: number, shipment: { carrier: string; trackingNumber: string; trackingUrl?: string }) =>
      send<{ order: Order }>(`/admin/orders/${number}/shipment`, post(shipment)),
    addNote: (number: number, text: string) => send<{ note: OrderNote }>(`/admin/orders/${number}/notes`, post({ text })),
    products: () => get<{ products: Product[] }>('/admin/products').then((r) => r.products),
    updateProduct: (id: string, patch: ProductPatch) => send<{ product: Product }>(`/admin/products/${encodeURIComponent(id)}`, put(patch)),
    setStock: (id: string, input: StockInput) => send<{ product: Product }>(`/admin/products/${encodeURIComponent(id)}/stock`, post(input)),
    adjustments: (id: string) => get<AdjustmentsResponse>(`/admin/products/${encodeURIComponent(id)}/adjustments`).then((r) => r.adjustments),
    customers: (cursor?: string) => get<CustomerPageResponse>(`/admin/customers${qs({ cursor })}`),
    customer: (email: string) => get<CustomerResponse>(`/admin/customers/${encodeURIComponent(email)}`),
    messages: () => get<MessagesResponse>('/admin/messages').then((r) => r.messages),
    setMessageStatus: (id: string, status: 'new' | 'done') => send<{ ok: true }>(`/admin/messages/${encodeURIComponent(id)}/status`, post({ status })),
    config: () => get<{ config: StoreSettings | null }>('/admin/config').then((r) => r.config),
    saveConfig: (patch: Partial<StoreSettings>) => send<{ config: StoreSettings }>('/admin/config', put(patch)).then((r) => r.config),
  };
}

export type AdminApi = ReturnType<typeof adminApi>;
